import { useEffect, useState, useCallback } from "react";
import { openDB } from "idb";
import { fetchEventSource } from "@microsoft/fetch-event-source";

export function useEmployeeStream(url) {
  const [employees, setEmployees] = useState([]);
  const [isConnected, setIsConnected] = useState(false);
  const [controller, setController] = useState(null);

  // 🔹 Load employees (cache → SSE)
  const loadEmployees = useCallback(async () => {
    const db = await openDB("EmployeeDB", 1, {
      upgrade(db) {
        if (!db.objectStoreNames.contains("employees")) {
          db.createObjectStore("employees", { keyPath: "eid" });
        }
      },
    });

    // Load cached employees first
    const allCached = await db.getAll("employees");
    if (allCached.length > 0) {
      console.log("✅ Loaded employees from IndexedDB");
      setEmployees(allCached);
      return; // if cache exists, skip SSE
    }

    // Otherwise, start SSE
    const ctrl = new AbortController();
    setController(ctrl);

    const token = localStorage.getItem("access_token");

    await fetchEventSource(url, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${token}`,
      },
      signal: ctrl.signal,
      onopen: () => {
        console.log("🔌 Connected to employee stream");
        setIsConnected(true);
      },
      onmessage: async (event) => {
        try {
          const chunk = JSON.parse(event.data);
          setEmployees((prev) => [...prev, ...chunk]);

          // Save chunk into IndexedDB
          const tx = db.transaction("employees", "readwrite");
          const store = tx.objectStore("employees");
          for (const emp of chunk) {
            await store.put(emp);
          }
          await tx.done;
        } catch (err) {
          console.error("❌ Error parsing SSE chunk:", err);
        }
      },
      onerror: (err) => {
        console.error("⚠️ SSE error:", err);
        setIsConnected(false);
        ctrl.abort();
      },
    });
  }, [url]);

  useEffect(() => {
    loadEmployees();
    return () => {
      if (controller) controller.abort();
    };
  }, [loadEmployees]);

  // 🔹 Create employee in cache + state
  const createEmployee = async (newEmp) => {
    const db = await openDB("EmployeeDB", 1);
    const tx = db.transaction("employees", "readwrite");
    await tx.objectStore("employees").add(newEmp);
    await tx.done;

    setEmployees((prev) => [...prev, newEmp]);
    console.log(`✅ Employee ${newEmp.eid} created in cache`);
  };

  // 🔹 Update employee in cache + state
  const updateEmployee = async (updatedEmp) => {
    const db = await openDB("EmployeeDB", 1);
    const tx = db.transaction("employees", "readwrite");
    await tx.objectStore("employees").put(updatedEmp);
    await tx.done;

    setEmployees((prev) =>
      prev.map((emp) => (emp.eid === updatedEmp.eid ? updatedEmp : emp))
    );
    console.log(`✅ Employee ${updatedEmp.eid} updated in cache`);
  };

  // 🔹 Clear cache + restart SSE
  const clearCache = async () => {
    const db = await openDB("EmployeeDB", 1);
    const tx = db.transaction("employees", "readwrite");
    await tx.objectStore("employees").clear();
    await tx.done;

    console.log("🗑️ Employee cache cleared");
    setEmployees([]);

    // Restart stream after clearing
    if (controller) controller.abort();
    await loadEmployees();
  };

  return { employees, isConnected, createEmployee, updateEmployee, clearCache };
}
